/**
 * @file experience.service.ts
 * @path apps/website/src/features/experiences/experience.service.ts
 * @description Normalizes Activity + Service data into unified ExperienceSummary and ExperienceDetail shapes.
 */

import { activitiesData } from "@/data/activities";
import { services } from "@/data/services";
import { Activity, Service } from "@sirbastians/types";
import { AlarmFill, GeoAltFill, People } from "react-bootstrap-icons";
import { ExperienceDetail, ExperienceSummary } from "./experience.types";
import { makeLink } from "@/config";

export class ExperienceService {
  /**
   * Returns all experiences in lightweight summary format.
   */
  static getAllExperiences(): ExperienceSummary[] {
    const fromActivities: ExperienceSummary[] = activitiesData.map((a) => ({
      id: `act-${a.id}`,
      slug: a.slug,
      _type: "activity",
      title: a.title,
      shortDescription: a.shortDescription,
      category: a.category,
      status: a.status,
      image: a.gallery.primary,
      price: {
        amount: a.pricing.amount,
        currency: a.pricing.currency,
        isFromPrice: false,
      },
      duration: a.duration,
      location: a.location,
      difficulty: a.difficulty,
      iconName: a.iconName,
    }));

    const fromServices: ExperienceSummary[] = services.map((s) => ({
      id: `srv-${s.id}`,
      slug: s.slug,
      _type: "service",
      title: s.title,
      shortDescription: s.shortDescription,
      category: s.category,
      status: s.status,
      image: s.gallery.primary,
      price: {
        amount: s.pricing.amount,
        currency: "ZAR",
        isFromPrice: s.pricing.type === "from",
      },
      duration:
        s.duration ??
        s.details.quickInfo.find((q) => q.label === "Duration")?.value,
      location:
        s.location ??
        s.details.quickInfo.find(
          (q) => q.label === "Area" || q.label === "Route",
        )?.value,
    }));

    return [...fromActivities, ...fromServices];
  }

  /**
   * Finds a specific experience by slug and returns a rich detail object.
   */
  static getExperienceBySlug(slug: string): ExperienceDetail | null {
    const activity = activitiesData.find((a) => a.slug === slug);
    const service = services.find((s) => s.slug === slug);
    const raw = activity || service;

    if (!raw) return null;

    const isActivity = !!activity;
    const act = activity as Activity;
    const srv = service as Service;

    return {
      slug: raw.slug,
      title: raw.title,
      subtitle: isActivity
        ? act.shortDescription
        : srv.subtitle || srv.shortDescription,
      description: raw.longDescription,
      category: raw.category,
      mainImage: {
        src: isActivity ? act.gallery.primary : srv.gallery.primary,
        alt: raw.title,
      },
      gallery: !isActivity
        ? srv.gallery.additional.map((img: string) => ({
            src: img,
            alt: raw.title,
          }))
        : [],
      price: {
        amount: isActivity ? act.pricing.amount : srv.pricing.amount,
        currency: isActivity ? act.pricing.currency : "ZAR",
        prefix: !isActivity && srv.pricing.type === "from" ? "from" : undefined,
        suffix: isActivity
          ? act.pricing.type === "per-person"
            ? "/ person"
            : ""
          : "",
      },
      metadata: this.extractMetadata(raw, isActivity),
      inclusions: raw.inclusions || [],
      exclusions: raw.exclusions || [],
      bookingLink:
        !isActivity && srv.externalBookingUrl
          ? srv.externalBookingUrl
          : makeLink.booking(raw.slug),
      breadcrumbPaths: ["Home", "Experiences"],

      // --- Extended fields ---
      highlights: !isActivity ? srv.highlights : undefined,
      audience: isActivity ? act.audience : srv.audience,
      itinerary: !isActivity ? srv.details.itinerary : undefined,
      faq: !isActivity ? srv.details.faq : undefined,
      whatToBring: isActivity ? act.whatToBring : undefined,
      cancellationPolicy: !isActivity ? srv.cancellationPolicy : undefined,
      pricingOptions: !isActivity ? srv.pricing.options : undefined,
      vipOptions:
        !isActivity && srv.vipOptions
          ? srv.vipOptions.map((v) => ({
              label: v.label,
              description: v.description,
              price: v.price,
            }))
          : undefined,
      tags: !isActivity ? srv.tags : undefined,
      difficulty: isActivity ? act.difficulty : undefined,
      routes:
        !isActivity && srv.routes
          ? srv.routes.map((r) => ({
              name: r.name,
              status: r.status,
              description: r.description,
            }))
          : undefined,
      capacity: !isActivity
        ? (srv.capacity ??
          srv.details.quickInfo.find(
            (q) => q.label === "Capacity" || q.label === "Group Size",
          )?.value)
        : undefined,
    };
  }

  private static extractMetadata(
    item: Activity | Service,
    isActivity: boolean,
  ) {
    const meta = [];

    if (isActivity) {
      const act = item as Activity;
      if (act.duration)
        meta.push({ icon: AlarmFill, label: "Duration", value: act.duration });
      if (act.location)
        meta.push({ icon: GeoAltFill, label: "Location", value: act.location });
    } else {
      const srv = item as Service;
      const dur =
        srv.duration ??
        srv.details.quickInfo.find((q) => q.label === "Duration")?.value;
      const loc =
        srv.location ??
        srv.details.quickInfo.find(
          (q) => q.label === "Area" || q.label === "Route",
        )?.value;
      const cap =
        srv.capacity ??
        srv.details.quickInfo.find(
          (q) => q.label === "Capacity" || q.label === "Group Size",
        )?.value;

      if (dur) meta.push({ icon: AlarmFill, label: "Duration", value: dur });
      if (loc) meta.push({ icon: GeoAltFill, label: "Location", value: loc });
      if (cap) meta.push({ icon: People, label: "Capacity", value: cap });
    }

    return meta;
  }
}
